import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const LoyaltyProgram = ({ loyaltyData, onRedeemReward, onReferFriend }) => {
  const [selectedReward, setSelectedReward] = useState(null);

  const progressPercentage = (loyaltyData?.currentPoints / loyaltyData?.nextTierPoints) * 100;

  const getTierColor = (tier) => {
    switch (tier) {
      case 'bronze':
        return 'text-amber-600 bg-amber-50 border-amber-200';
      case 'silver':
        return 'text-gray-600 bg-gray-50 border-gray-200';
      case 'gold':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'platinum':
        return 'text-purple-600 bg-purple-50 border-purple-200';
      default:
        return 'text-text-secondary bg-muted border-border';
    }
  };

  const handleRedeemReward = (rewardId) => {
    onRedeemReward(rewardId);
    setSelectedReward(null);
  };

  const handleShareReferral = (platform) => {
    const referralUrl = `https://clean360.com/ref/${loyaltyData?.referralCode}`;
    const message = `Join Clean360 and get $20 off your first service! Use my referral code: ${loyaltyData?.referralCode}`;
    
    switch (platform) {
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referralUrl)}&quote=${encodeURIComponent(message)}`, '_blank');
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(referralUrl)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(message + ' ' + referralUrl)}`, '_blank');
        break;
      case 'email':
        window.location.href = `mailto:?subject=Join Clean360&body=${encodeURIComponent(message + '\n\n' + referralUrl)}`;
        break;
      default:
        navigator.clipboard?.writeText(referralUrl);
        alert('Referral link copied to clipboard!');
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-elevation-2 border border-border">
      <div className="p-6 border-b border-border">
        <h2 className="text-xl font-semibold text-text-primary">
          Loyalty Program
        </h2>
      </div>
      <div className="p-6 space-y-6">
        {/* Current Status */}
        <div className="text-center">
          <div className="w-20 h-20 bg-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="Award" size={32} color="white" />
          </div>
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getTierColor(loyaltyData?.currentTier)}`}>
            <Icon name="Crown" size={16} className="mr-1" />
            {loyaltyData?.currentTier?.charAt(0)?.toUpperCase() + loyaltyData?.currentTier?.slice(1)} Member
          </div>
          <div className="mt-4">
            <div className="text-3xl font-bold text-text-primary">
              {loyaltyData?.currentPoints?.toLocaleString()}
            </div>
            <div className="text-text-secondary">Points Available</div>
          </div>
        </div>

        {/* Progress to Next Tier */}
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-text-primary">
              Progress to {loyaltyData?.nextTier?.charAt(0)?.toUpperCase() + loyaltyData?.nextTier?.slice(1)}
            </span>
            <span className="text-sm text-text-secondary">
              {loyaltyData?.nextTierPoints - loyaltyData?.currentPoints} points to go
            </span>
          </div>
          <div className="w-full bg-border rounded-full h-2">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${Math.min(progressPercentage, 100)}%` }}
            />
          </div>
        </div>

        {/* Available Rewards */}
        <div>
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Available Rewards
          </h3>
          
          {loyaltyData?.availableRewards?.length === 0 ? (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-muted rounded-full flex items-center justify-center mx-auto mb-3">
                <Icon name="Gift" size={24} color="var(--color-text-secondary)" />
              </div>
              <p className="text-text-secondary">
                No rewards available. Earn more points to unlock rewards!
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {loyaltyData?.availableRewards?.map((reward) => (
                <div
                  key={reward?.id}
                  className="border border-border rounded-lg p-4 hover:shadow-elevation-1 transition-shadow duration-200"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <h4 className="font-medium text-text-primary">{reward?.title}</h4>
                      <p className="text-sm text-text-secondary mt-1">{reward?.description}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-primary">
                        {reward?.pointsCost}
                      </div>
                      <div className="text-xs text-text-secondary">points</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-text-secondary">
                      Expires: {new Date(reward.expiryDate)?.toLocaleDateString()}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleRedeemReward(reward?.id)}
                      disabled={loyaltyData?.currentPoints < reward?.pointsCost}
                      iconName="Gift"
                      iconPosition="left"
                    >
                      Redeem
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Referral Program */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Refer Friends & Earn
          </h3>
          
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                <Icon name="Users" size={20} color="white" />
              </div>
              <div>
                <div className="font-medium text-text-primary">
                  Earn {loyaltyData?.referralReward} points per referral
                </div>
                <div className="text-sm text-text-secondary">
                  Your friend gets $20 off their first service
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 mb-3">
              <div className="flex-1 bg-white border border-border rounded px-3 py-2 text-sm font-mono">
                {loyaltyData?.referralCode}
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="Copy"
                onClick={() => handleShareReferral('copy')}
              />
            </div>
            
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                iconName="Facebook"
                onClick={() => handleShareReferral('facebook')}
              />
              <Button
                variant="outline"
                size="sm"
                iconName="Twitter"
                onClick={() => handleShareReferral('twitter')}
              />
              <Button
                variant="outline"
                size="sm"
                iconName="MessageSquare"
                onClick={() => handleShareReferral('whatsapp')}
              />
              <Button
                variant="outline"
                size="sm"
                iconName="Mail"
                onClick={() => handleShareReferral('email')}
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-text-primary">
                {loyaltyData?.referralStats?.totalReferrals}
              </div>
              <div className="text-sm text-text-secondary">Total Referrals</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-text-primary">
                {loyaltyData?.referralStats?.pointsEarned?.toLocaleString()}
              </div>
              <div className="text-sm text-text-secondary">Points Earned</div>
            </div>
          </div>
        </div>

        {/* Points History */}
        <div className="border-t border-border pt-6">
          <h3 className="text-lg font-medium text-text-primary mb-4">
            Recent Activity
          </h3>
          
          <div className="space-y-3">
            {loyaltyData?.recentActivity?.map((activity) => (
              <div key={activity?.id} className="flex items-center justify-between py-2">
                <div className="flex items-center space-x-3">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    activity?.type === 'earned' ? 'bg-success/10' : 'bg-error/10'
                  }`}>
                    <Icon 
                      name={activity?.type === 'earned' ? 'Plus' : 'Minus'} 
                      size={16} 
                      color={activity?.type === 'earned' ? 'var(--color-success)' : 'var(--color-error)'}
                    />
                  </div>
                  <div>
                    <div className="text-sm font-medium text-text-primary">
                      {activity?.description}
                    </div>
                    <div className="text-xs text-text-secondary">
                      {new Date(activity.date)?.toLocaleDateString()}
                    </div>
                  </div>
                </div>
                <div className={`text-sm font-medium ${
                  activity?.type === 'earned' ? 'text-success' : 'text-error'
                }`}>
                  {activity?.type === 'earned' ? '+' : '-'}{activity?.points}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoyaltyProgram;